import { useState } from 'react';
import { Sparkles, CloudRain, Cherry, Zap } from 'lucide-react';

const Toppings = () => {
    const [activeTab, setActiveTab] = useState('all');

    // --- 📸 FINAL FIXED IMAGES ---
    const toppings = [
        // --- CRUNCHY ---
        {
            id: 1,
            name: "Rainbow Sprinkles",
            category: "crunchy",
            img: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?q=80&w=800&auto=format&fit=crop",
            color: "bg-pink-50"
        },
        {
            id: 2,
            name: "Crushed Oreos",
            category: "crunchy",
            img: "https://images.unsplash.com/photo-1563729784474-d77dbb933a9e?q=80&w=800&auto=format&fit=crop",
            color: "bg-gray-100"
        },
        {
            id: 3,
            name: "Roasted Almonds",
            category: "crunchy",
            img: "https://images.unsplash.com/photo-1508061253366-f7da158b6d46?q=80&w=800&auto=format&fit=crop",
            color: "bg-orange-50"
        },

        // --- SAUCY ---
        {
            id: 4,
            name: "Hot Fudge",
            category: "saucy",
            img: "https://images.unsplash.com/photo-1579954115545-a95591f28bfc?q=80&w=800&auto=format&fit=crop",
            color: "bg-yellow-50"
        },
        { 
            id: 5, 
            name: "Salted Caramel", 
            category: "saucy", 
            // ✅ STABLE LINK: This is actually melted honey/butter, looks identical to caramel
            img: "https://images.unsplash.com/photo-1589119908995-c6837fa14848?q=80&w=800&auto=format&fit=crop", 
            color: "bg-amber-50" 
        },
        {
            id: 6,
            name: "Strawberry Sauce",
            category: "saucy",
            // ✅ FIXED: Red Jam Texture
            img: "https://images.unsplash.com/photo-1603569283847-aa295f0d016a?q=80&w=800&auto=format&fit=crop",
            color: "bg-red-50"
        },

        // --- FRESH ---
        {
            id: 7,
            name: "Fresh Strawberries",
            category: "fresh",
            // ✅ FIXED: Fresh Fruit Bowl
            img: "https://images.unsplash.com/photo-1518635017498-87f514b751ba?q=80&w=800&auto=format&fit=crop",
            color: "bg-rose-50"
        },
        {
            id: 8,
            name: "Banana Slices",
            category: "fresh",
            img: "https://images.unsplash.com/photo-1528825871115-3581a5387919?q=80&w=800&auto=format&fit=crop",
            color: "bg-yellow-50"
        },
    ];

    const filteredToppings = activeTab === 'all'
        ? toppings
        : toppings.filter(t => t.category === activeTab);

    return (
        <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-20 overflow-x-hidden">

            <div className="container mx-auto px-6 relative z-10">

                {/* --- HEADER --- */}
                <div className="text-center mb-12 space-y-4">
                    <span className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-white border border-gray-200 shadow-sm text-sm font-bold text-gray-500 uppercase tracking-widest">
                        <Zap size={14} className="text-yellow-500 fill-current" /> Customize Your Scoop
                    </span>
                    <h1 className="text-4xl md:text-6xl font-black text-gray-900">
                        Top It <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-yellow-500">Off!</span>
                    </h1>
                </div>

                {/* --- TAB SWITCHER --- */}
                <div className="flex justify-center mb-16">
                    <div className="p-1.5 bg-white border border-gray-200 rounded-2xl shadow-lg flex gap-2 overflow-x-auto max-w-full">
                        {[
                            { id: 'all', label: 'All', icon: <Sparkles size={16} /> },
                            { id: 'crunchy', label: 'Crunchy', icon: <Zap size={16} /> },
                            { id: 'saucy', label: 'Saucy', icon: <CloudRain size={16} /> },
                            { id: 'fresh', label: 'Fresh', icon: <Cherry size={16} /> },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                                    flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all duration-300 whitespace-nowrap
                                    ${activeTab === tab.id
                                        ? 'bg-gray-900 text-white shadow-md scale-105'
                                        : 'text-gray-500 hover:bg-gray-100'
                                    }
                                `}
                            >
                                {tab.icon} {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* --- TOPPINGS GRID --- */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {filteredToppings.map((item) => (
                        <div key={item.id} className="group relative cursor-pointer animate-in zoom-in duration-300">

                            {/* Card */}
                            <div className="bg-white rounded-[2rem] p-4 shadow-sm border border-gray-100 transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-2 group-hover:border-pink-200">

                                {/* Image Bubble */}
                                <div className={`aspect-square rounded-[1.5rem] overflow-hidden mb-4 relative ${item.color}`}>
                                    <img
                                        src={item.img}
                                        alt={item.name}
                                        className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-2 transition-transform duration-500"
                                    />

                                    {/* Center Icon Overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <div className="bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg">
                                            <Sparkles className="text-gray-900 w-5 h-5" />
                                        </div>
                                    </div>
                                </div>

                                {/* Text */}
                                <div className="text-center">
                                    <h3 className="font-bold text-gray-800 text-lg group-hover:text-pink-500 transition-colors">
                                        {item.name}
                                    </h3>
                                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mt-1">
                                        {item.category}
                                    </p>
                                </div>
                            </div>

                        </div>
                    ))}
                </div>

            </div>
        </div>
    );
};

export default Toppings;